// BOU-1654: audio-playback observability. This module is the ONLY Sentry
// touchpoint for the client audio pipeline — call sites (hooks/components)
// import from here, never from ./sentry.js directly, so every audio signal
// stays structured and low-cardinality.
//
// Both exports are side-effect only and must never throw or otherwise affect
// playback control flow: a telemetry failure is never allowed to break audio.
import { addBreadcrumb, recordMeasurement } from './sentry.js';

// Vite-style DEV gate, matching the pattern used elsewhere in this codebase
// (e.g. PlayerRoomShell.jsx, useTurnSubmit.js).
const isDev = Boolean(import.meta.env?.DEV);

/**
 * Record a low-cardinality breadcrumb for the audio playback pipeline.
 *
 * @param {string} subcategory - short event name, e.g. 'suppression', 'fetch',
 *   'audio-available', 'cursor-advance', 'shell-hold', 'chunk-gap'.
 * @param {Object} [data] - structured payload (ids, reasons, counts — no PII).
 */
export function addAudioBreadcrumb(subcategory, data = {}) {
  try {
    addBreadcrumb({
      category: 'audio.playback',
      message: subcategory,
      data,
      level: 'info',
    });
  } catch {
    // Telemetry must never break playback.
  }
  if (isDev) {
    try {
      console.debug(`[AUDIO_TELEM] ${subcategory}`, data);
    } catch {
      // Telemetry must never break playback.
    }
  }
}

/**
 * Record a timing measurement (TTFA, inter-chunk gap, …). Reports to Sentry
 * as a span measurement when tracing is active, and always mirrors as an
 * audio breadcrumb so the value is visible in the breadcrumb trail even when
 * tracing is disabled/unsampled.
 *
 * @param {string} name - measurement name, e.g. 'audio.ttfa', 'audio.inter_chunk_gap'.
 * @param {number} valueMs - measured duration in milliseconds.
 * @param {Object} [data] - additional low-cardinality context (requestId, cardId, seq…).
 */
export function recordAudioMeasurement(name, valueMs, data = {}) {
  try {
    // BOU-2270: forward the low-cardinality context as span attributes so the
    // measurement is segmentable in Discover. `seq` is the important one — the
    // narration gap is concentrated at the segment 0->1 boundary, and an
    // aggregate that mixes every boundary together hides it.
    recordMeasurement(name, valueMs, 'millisecond', buildMeasurementAttributes(data));
  } catch {
    // Telemetry must never break playback.
  }
  addAudioBreadcrumb(name, { valueMs, ...data });
}

// Only primitives, and only fields that are bounded in cardinality. requestId is
// deliberately excluded from span attributes (it is per-request and would blow up
// the attribute index); it stays on the breadcrumb where it is useful for
// single-session forensics.
function buildMeasurementAttributes(data) {
  const attributes = {};
  if (Number.isFinite(data?.seq)) {
    attributes['audio.seq'] = data.seq;
    // The 0->1 boundary is the one the BOU-2270 symptom lives at; make it a
    // first-class boolean so it can be filtered without a range query.
    attributes['audio.is_opening_boundary'] = data.seq === 1;
  }
  // TTFA decomposition. A duration alone cannot tell a slow pipeline apart from
  // a wake that raced the queue and waited on a retry, and those want opposite
  // fixes. Both are bounded in cardinality (a small integer and a boolean).
  if (Number.isFinite(data?.fetchAttempts)) {
    attributes['audio.fetch_attempts'] = data.fetchAttempts;
  }
  if (typeof data?.firstFetchHit === 'boolean') {
    attributes['audio.first_fetch_hit'] = data.firstFetchHit;
  }
  return attributes;
}
